import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { WALMART_MARKETPLACE_CHANNEL_ID } from '../../constant';
import { getLocalStorageItem, removeLocalStorageItem } from '../../services/StorageService';
import { validateAuthCode } from '../../store/actions/marketplace.action';

/**
 * WalmartSuccess Component
 * Called after Node adapter successfully exchanges code for tokens and saves them to Python.
 * This component saves the remaining config fields using the same validateAuthCode action as other marketplaces.
 */
const WalmartSuccess = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('loading');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        saveWalmartConfig();
    }, []);

    const saveWalmartConfig = async () => {
        try {
            // Get identity fields from URL params (adapter includes these from decoded state)
            const urlCompanySellerId = searchParams.get('company_seller_id');
            const urlSellerId = searchParams.get('seller_id');
            const urlCompanyId = searchParams.get('company_id');

            // Get saved config from localStorage (saved before OAuth redirect)
            // Uses 'redirect' key to match existing pattern from HandleRedirection
            const formData = JSON.parse(getLocalStorageItem('redirect') || '{}');

            // Build payload with snake_case field names (required by Python API - uses request_data["key"] syntax)
            const payload = {
                // Identity fields - URL params (from adapter) take priority for Flow 2
                seller_id: urlSellerId || formData.seller_id || '',
                company_id: urlCompanyId || formData.company_id || '',
                company_seller_id: urlCompanySellerId || formData.company_seller_id || '',
                channel_id: formData.channel_id || String(WALMART_MARKETPLACE_CHANNEL_ID),
                integration_name: formData.integration_name || 'Walmart',
                integration_id: formData.integration_id || '',

                // Auth fields
                auth_code: 'WALMART_SSO_COMPLETED',
                refresh_token: formData.refresh_token || '',
                order_quantity: formData.order_quantity || '0',

                // Configuration fields from form (defaults for Flow 2)
                send_confirmation_mail: formData.send_confirmation_mail || false,
                active: formData.active !== undefined ? formData.active : true,
                skip_address_pre_verification: formData.skip_address_pre_verification || false,
                include_packing_slips_in_confirmation_email: formData.include_packing_slips_in_confirmation_email || false,
                load_item_dimensions: formData.load_item_dimensions || false,
                auto_refresh: formData.auto_refresh || false,
                action_type: formData.action_type || 'internally_fulfilled',
                number_of_days_of_orders: formData.number_of_days_of_orders || '14',
                pending_shipment_statuses: formData.pending_shipment_statuses || 'Completed',

                // Additional required fields (Python uses request_data["key"] syntax)
                marketplace_dropdown_url: formData.marketplace_dropdown_url || '',
                ignore_weight_checkbox: formData.ignore_weight_checkbox || false,
                ignore_package_weight_checkbox: formData.ignore_package_weight_checkbox || false,
                save_asin_as_sku_checkbox: formData.save_asin_as_sku_checkbox || false,
                redownload_all_orders_on_next_sync_checkbox: formData.redownload_all_orders_on_next_sync_checkbox || false,

                // Shopify/Store fields (camelCase as per Python backend)
                storeName: formData.storeName || 'Walmart Store',
                accessToken: formData.accessToken || '',
                storeUrl: formData.storeUrl || '',

                // Address overrides (structure stays the same)
                senderAddress: formData.senderAddress || {
                    Override_Company_and_Name: { isActive: false, Company: '', Name: '' },
                    Override_Address: { isActive: false, Address_1: '', Address_2: '', City: '', State: '', Zip: '', Country: '' },
                    Override_Phone: { isActive: false, Phone: '' },
                    Override_Email: { isActive: false, Email: '' }
                },
                returnAddress: formData.returnAddress || {
                    Override_Return_To_Address: { isActive: false, Company: '', Name: '', Address_1: '', Address_2: '', City: '', State: '', Zip: '', Country: '', Phone: '', Email: '' }
                },
            };

            // Use existing validateAuthCode action (same as other marketplaces)
            const response = await validateAuthCode(payload);

            // Check for success - Status 200 or Entity with integrationId
            if (response?.data?.Status === 200 || response?.data?.Entity?.integrationId) {
                // Clear localStorage after successful save (same as HandleRedirection)
                removeLocalStorageItem('redirect');
                setStatus('success');
            } else {
                setStatus('error');
                setErrorMessage(response?.data?.Message?.AppStatusDescription || response?.data?.Message || 'Failed to save configuration');
            }
        } catch (error) {
            console.error('Error saving Walmart config:', error);
            setStatus('error');
            setErrorMessage(error?.response?.data?.Message || error.message || 'Failed to save configuration');
        }
    };

    const handleRedirectionClick = () => {
        removeLocalStorageItem('redirect');
        navigate('/marketplace-integration');
    };

    return (
        <main>
            <div className="main__container">
                <div className="cus-page-content-sec">
                    <div className="top_right_button_container cus-content-only-sec">
                        <div className='incoming_wrapper main_content_table addressDetailWrapper'>
                            {status === 'loading' && (
                                <div className='statusRedirect'>
                                    <div className='icon'>
                                        <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                                    </div>
                                    <h2 className='mb-10'>Saving Configuration...</h2>
                                    <p>Please wait while we complete the setup.</p>
                                </div>
                            )}
                            {status === 'success' && (
                                <div className='statusRedirect'>
                                    <div className='icon'>
                                        <i className="fa fa-check" aria-hidden="true"></i>
                                    </div>
                                    <h2 className='mb-10'>Access Granted</h2>
                                    <p>Your Walmart account has been connected successfully.</p>
                                    <button
                                        className="btn cus-primary-transparent-btn"
                                        onClick={handleRedirectionClick}
                                    >
                                        Back to Integration
                                    </button>
                                </div>
                            )}
                            {status === 'error' && (
                                <div className='statusRedirect error'>
                                    <div className='icon error'>
                                        <i className="fa fa-times" aria-hidden="true"></i>
                                    </div>
                                    <h2 className='mb-10'>Configuration Failed</h2>
                                    <p>{errorMessage}</p>
                                    <button
                                        className="btn cus-primary-transparent-btn"
                                        onClick={handleRedirectionClick}
                                    >
                                        Back to Integration
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default WalmartSuccess;
